import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { getFontSize } from "@/react/context/bryntumContext";

export const ChecklistTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { editorHandler } = useSwivuBryntumContext();
  const checklists = data as Checklist[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: checklists.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleChecklistClick = (checklist: Checklist) => {
    const entity = checklist.entity;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    editorHandler.openEditor(EDITOR_KEYS.checklistEditor, null, {
      entity,
      checklistId: checklist.id
    }, { by: "recordType", useExisting: true });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {checklists.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            <p className="text-sm">No checklists found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const checklist = checklists[virtualRow.index];
              return (
                <div
                  key={checklist.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleChecklistClick(checklist)}
                  >
                    <div className="font-medium text-foreground line-clamp-1">{checklist.title || "N/A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
